<template>
  <Dialog
    v-model:visible="isVisible"
    :modal="true"
    :style="{ width: '450px' }"
    :header="t('campaignsAutomationList.actions.duplicate')"
  >
    <div class="p-fluid">
      <p>{{ t('campaignsAutomationList.actions.duplicateConfirm') }}</p>
      <div class="field mt-3">
        <InputText
          v-model="newCampaignName"
          type="text"
          class="w-full"
          :placeholder="t('campaignsAutomationList.actions.duplicateNamePlaceholder')"
        />
      </div>
    </div>
    <template #footer>
      <SpmButton
        :label="t('cancel')"
        icon="pi pi-times"
        class-style="p-button p-button-secondary"
        @click="close"
      />
      <SpmButton
        :label="t('validate')"
        icon="pi pi-check"
        class-style="p-button p-button-success"
        :disabled="isLoading || !newCampaignName.trim()"
        @click="confirm"
      />
    </template>
  </Dialog>
</template>

<script lang="ts">
import { defineComponent, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import SpmButton from '@/components/spm-primevue/SpmButton.vue';
import { showToastError, showToastSuccess } from '@/helpers';
import { UserState } from '@/composables/User';
import { TypeCampaignEnum } from '@/types';
import {
  setRefreshScenariosList,
} from '@/composables/AutomatedScenarios';
import { duplicateFlow, fetchFlowOperators } from '@/composables/flows/useFlowApi';
import { getNotConfiguredServices } from '@/composables/shop/MyShopParameters';
import { store } from '@/store';
import type { AutomationCampaignData, CampaignActionPayload, DuplicateCampaignResult } from './types';

export default defineComponent({
  name: 'DuplicateCampaignAction',

  components: {
    Dialog,
    InputText,
    SpmButton,
  },

  emits: ['confirm'],

  setup(_, { emit, expose }) {
    const { t } = useI18n();

    // Internal state
    const isVisible = ref(false);
    const isLoading = ref(false);
    const currentCampaign = ref<AutomationCampaignData | null>(null);
    const newCampaignName = ref('');
    const idShop = ref<number>(UserState.activeShop?.id ?? 0);

    /**
     * Open the dialog with the given payload
     */
    const open = (payload: CampaignActionPayload) => {
      currentCampaign.value = payload.campaign;
      newCampaignName.value = `${t('myLists.manage.actions.cloneText')}${payload.campaign.name}`;
      isVisible.value = true;
    };

    /**
     * Close the dialog
     */
    const close = () => {
      isVisible.value = false;
      currentCampaign.value = null;
      newCampaignName.value = '';
    };

    /**
     * Execute the duplication
     */
    const executeDuplication = async () => {
      if (!currentCampaign.value) return;

      const result = await duplicateFlow(TypeCampaignEnum.AUTOMATION, currentCampaign.value.id, newCampaignName.value);

      if (result.success && result.id) {
        showToastSuccess(t('campaignsAutomationList.actions.duplicateSuccess'));
        setRefreshScenariosList(true);

        const emitResult: DuplicateCampaignResult = {
          campaign: currentCampaign.value,
          newName: newCampaignName.value,
          success: true,
          newCampaignId: result.id,
        };

        emit('confirm', emitResult);
        close();
      } else {
        showToastError(t('campaignsAutomationList.actions.duplicateError'));
      }
    };

    /**
     * Confirm the action and emit result
     */
    const confirm = async () => {
      if (!currentCampaign.value || !newCampaignName.value.trim()) return;

      isLoading.value = true;

      try {
        // Check for configured services
        const operators = await fetchFlowOperators(
          TypeCampaignEnum.AUTOMATION,
          currentCampaign.value.id,
          ['boxsendfbmessenger'],
        );
        const notConfiguredServices = await getNotConfiguredServices(
          idShop.value,
          ['editScenarioCampaign', ...operators],
        );

        if (notConfiguredServices.length) {
          store.commit('general/setNotConfiguredServices', notConfiguredServices);
          store.commit('general/setIsServicesParametersModalVisible', true);
          store.commit('general/setServiceParameterSuccessAction', executeDuplication);
          close();
        } else {
          await executeDuplication();
        }
      } catch (err: unknown) {
        const errorMessage = err instanceof Error ? err.message : t('errorMessages.GENERIC_ERROR');
        showToastError(errorMessage);
      } finally {
        isLoading.value = false;
      }
    };

    // Expose open method for parent component
    expose({ open });

    return {
      t,
      isVisible,
      isLoading,
      newCampaignName,
      close,
      confirm,
    };
  },
});
</script>
